import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useOrg, useTryonEnabled } from '../context/OrgContext';

interface Props {
  searchText: string;
  setSearchText: (v: string) => void;
}

const Navbar: React.FC<Props> = ({ searchText, setSearchText }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, openLogin, logout } = useAuth();
  const { cartCount } = useCart();
  const org = useOrg();
  const tryonEnabled = useTryonEnabled();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const customNavLinks = org?.ecommerce_headers?.navLinks || [];

  const defaultMiddleLinks = [
    { label: 'Eyeglasses', to: '/products?category=1', key: 'eyeglasses' },
    { label: 'Kids Glasses', to: '/products?ideal_for=3', key: 'kids_glasses' },
    { label: 'Contact Lenses', to: '/products?category=4', key: 'contact_lenses' },
    { label: 'Sunglasses', to: '/products?category=3', key: 'sunglasses' },
    { label: 'Blue Light Glasses', to: '/products?category=6', key: 'blue_light_glasses' },
    { label: 'Accessories', to: '/products?category=5', key: 'accessories' },
  ];

  const mappedMiddleLinks = customNavLinks.length > 0
    ? customNavLinks.map(link => ({
        label: link.label,
        to: `/products?${link.filterType}=${link.filterValue}`,
        key: link.id || `custom_${Math.random()}`
      }))
    : defaultMiddleLinks;

  const navLinks = [
    { label: 'Home', to: '/', key: 'home' },
    ...mappedMiddleLinks,
    ...(tryonEnabled ? [{ label: 'Virtual Try-On', to: '/tryon', key: 'virtual_try_on' }] : []),
  ];

  const isActive = (to: string) => {
    const [path, query] = to.split('?');
    if (path === '/' ) return location.pathname === '/';
    if (location.pathname !== path) return false;
    if (!query) return true;
    return location.search === `?${query}`;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchText.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchText.trim())}`);
    } else {
      navigate('/products');
    }
    setSearchOpen(false);
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) {
      navigate('/products');
      return;
    }
    debounceRef.current = setTimeout(() => {
      navigate(`/products?search=${encodeURIComponent(val.trim())}`);
    }, 500);
  }, [navigate, setSearchText]);

  return (
    <header className="navbar">
      <div className="navbar__inner">
        {/* Logo */}
        <Link to="/" className="navbar__logo">
          {org?.ecommerce_logo || org?.image ? (
            <img
              src={org.ecommerce_logo || org.image!}
              alt={org?.org_name || "Store"}
              className="navbar__logo-img"
            />
          ) : (
            <>
              <img
                src="/image1.png"
                alt={org?.org_name || "Store"}
                className="navbar__logo-img"
              />
              <span className="navbar__logo-text">{org?.org_name || "Store"}</span>
            </>
          )}
        </Link>

        {/* Desktop nav */}
        <nav className="navbar__links">
          {navLinks.map((link) => (
            <Link key={link.label} to={link.to} className={`navbar__link${isActive(link.to) ? ' navbar__link--active' : ''}`}>
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Icons */}
        <div className="navbar__icons">
          {/* Search */}
          <button className="navbar__icon-btn" onClick={() => setSearchOpen(!searchOpen)} title="Search">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>

          {/* User */}
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button className="navbar__icon-btn" onClick={() => user ? setUserMenuOpen(!userMenuOpen) : openLogin()} title="Account">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            {userMenuOpen && user && (
              <div className="navbar__user-menu">
                <Link to="/profile" className="navbar__user-item" onClick={() => setUserMenuOpen(false)}>My Profile</Link>
                <Link to="/orders" className="navbar__user-item" onClick={() => setUserMenuOpen(false)}>My Orders</Link>
                <Link to="/wishlist" className="navbar__user-item" onClick={() => setUserMenuOpen(false)}>Wishlist</Link>
                <button className="navbar__user-item navbar__user-logout" onClick={logout}>Logout</button>
              </div>
            )}
          </div>

          {/* Wishlist */}
          <Link to="/wishlist" className="navbar__icon-btn" title="Wishlist">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </Link>

          {/* Cart */}
          <Link to="/cart" className="navbar__icon-btn navbar__cart-btn" title="Cart">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            {cartCount > 0 && <span className="navbar__cart-badge">{cartCount}</span>}
          </Link>

          {/* Mobile menu toggle */}
          <button className="navbar__icon-btn navbar__mobile-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {mobileMenuOpen
                ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
                : <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
                </>}
            </svg>
          </button>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="navbar__search-bar">
          <form onSubmit={handleSearch} className="navbar__search-form">
            <input
              type="text"
              placeholder="Search eyeglasses, sunglasses, brands..."
              value={searchText}
              onChange={handleSearchChange}
              className="navbar__search-input"
              autoFocus
            />
            <button type="submit" className="navbar__search-submit">Search</button>
            <button type="button" className="navbar__search-close" onClick={() => setSearchOpen(false)}>✕</button>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <nav className="navbar__mobile-menu">
          {navLinks.map((link) => (
            <Link key={link.label} to={link.to} className={`navbar__mobile-link${isActive(link.to) ? ' navbar__mobile-link--active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
              {link.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
